import { createEffect, createEvent, createStore, sample, combine } from "effector";
import type {
  AssistantAction,
  AssistantMessage,
  AssistantMarkdownMessage,
  AssistantMessageBlock,
  AssistantSuggestion,
} from "../types/assistant";
import type { Photo, Sight, SightId, Video } from "../types";
import type { PoiMarkerDiagnostics } from "../scene/createScene";
import {
  publishEventBusEvent,
  subscribeEventBus,
} from "../services/assistantClient";
import type {
  EventBusContext,
  EventBusEvent,
  EventBusMessage,
  EventBusRequest,
  AssistantMessageEvent,
  ThinkingEvent,
} from "../../server/src/shared/eventBus";
import {
  $appState,
  $sightsStore,
  $currentSight,
  $currentVirtualTourIndex,
  $currentVirtualTourStep,
  $totalVirtualTourSteps,
  $isVirtualTourActive,
  $isVirtualTourPaused,
  $orbitAnimationEnabled,
  $sceneBindings,
  goToSight,
  userSelectedSight,
  nextVirtualTourStep,
  previousVirtualTourStep,
  continueVirtualTour,
  setVirtualTour,
  setOrbitAnimationEnabled,
  stopVirtualTour,
  userInteracted,
} from "./appState";
import type { SightSelectionPayload } from "./appState";
import { updateAudioConfig, updateCameraConfig } from "./appConfig";
import { $locale } from "./locale";
import { formatMessage, getMessages } from "../i18n";
import type { Locale } from "../../server/src/shared/locale";
import { introMessageReceived } from "./onboarding";
import { kaabaSceneConfig } from "../scene/kaaba";

const normalizeTourData = (data: AssistantMessage[]): AssistantMessage[] =>
  (Array.isArray(data) ? data : [])
    .map((message) => {
      if (!message || typeof message !== "object") {
        return null;
      }
      return {
        blocks: Array.isArray(message.blocks) ? message.blocks : [],
        suggestions: Array.isArray(message.suggestions)
          ? message.suggestions
          : [],
        actions: Array.isArray(message.actions) ? message.actions : [],
      };
    })
    .filter((message): message is AssistantMessage => message !== null);

type SceneContextOptions = {
  currentSight: Sight | null;
  markers: PoiMarkerDiagnostics[];
};

type EventBusContextOptions = SceneContextOptions & {
  locale: Locale;
  sceneId: string | number | null;
};

const buildEventBusContext = ({
  currentSight,
  markers,
  locale,
  sceneId,
}: EventBusContextOptions): EventBusContext => {
  const markerDetails = markers.map((marker) => ({
    sightId: marker.sightId,
    visible: marker.visible,
    occluded: marker.occluded,
    distance: Number.isFinite(marker.distance)
      ? Number(marker.distance)
      : null,
  }));

  return {
    locale,
    sceneId: sceneId ?? "main",
    currentSight: currentSight?.id ?? null,
    markers: markerDetails,
  };
};

type EventBusMessageWithEvent<T extends EventBusEvent["type"]> = EventBusMessage & {
  event: Extract<EventBusEvent, { type: T }>;
};

const isThinkingMessage = (
  message: EventBusMessage
): message is EventBusMessageWithEvent<ThinkingEvent["type"]> =>
  message.event.type === "thinking";

const isAssistantMessage = (
  message: EventBusMessage
): message is EventBusMessageWithEvent<AssistantMessageEvent["type"]> =>
  message.event.type === "assistant_message";

export type AssistantHudStatus =
  | "idle"
  | "sending"
  | "awaiting"
  | "rendering"
  | "error";

export type AssistantHudMessage =
  | {
      id: string;
      role: "assistant";
      blocks: AssistantMessageBlock[];
      suggestions: AssistantSuggestion[];
      actions: AssistantAction[];
      disableAnimation?: boolean;
      requestId?: string | null;
    }
  | {
      id: string;
      role: "user";
      text: string;
      requestId?: string | null;
    };

const getInitialAssistantMessage = (locale = $locale.getState()) => {
  const i18n = getMessages(locale);
  return [
    {
      type: "paragraph",
      text: i18n.assistant.initialMessage,
    },
  ] satisfies AssistantMessageBlock[];
};

const createMessageId = (role: AssistantHudMessage["role"]) =>
  `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const ASSISTANT_SESSION_STORAGE_KEY = "pilgrim.sessionId";

const readStoredSessionId = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(ASSISTANT_SESSION_STORAGE_KEY);
    const trimmed = stored?.trim() ?? "";
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
};

const writeStoredSessionId = (sessionId: string) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(ASSISTANT_SESSION_STORAGE_KEY, sessionId);
  } catch {
    // ignore storage errors
  }
};

const createSessionId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getInitialSessionId = (): string | null => {
  const stored = readStoredSessionId();
  if (stored) {
    return stored;
  }
  const generated = createSessionId();
  writeStoredSessionId(generated);
  return generated;
};

const createRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isMatchingRequestId = (
  latestRequestId: string | null,
  requestId?: string | null
) => {
  if (!latestRequestId) {
    return true;
  }
  const normalized = requestId?.trim() ?? "";
  return normalized !== "" && normalized === latestRequestId;
};

const createUserMessage = (text: string, requestId?: string | null): AssistantHudMessage => ({
  id: createMessageId("user"),
  role: "user",
  text,
  requestId,
});

const toParagraphBlocks = (text: string): AssistantMessageBlock[] => [
  {
    type: "paragraph",
    text,
  },
];

type AnyAssistantMessage = AssistantMessage | AssistantMarkdownMessage;

const isMarkdownMessage = (
  message: AnyAssistantMessage
): message is AssistantMarkdownMessage =>
  "content" in message && typeof message.content === "string";

const normalizeMessageBlocks = (message: AnyAssistantMessage): AssistantMessageBlock[] => {
  if (isMarkdownMessage(message)) {
    return [{ type: "markdown", content: message.content, images: message.images ?? [] }];
  }
  return message.blocks ?? [];
};

const createAssistantMessage = (
  blocks: AssistantMessageBlock[],
  suggestions: AssistantSuggestion[],
  actions: AssistantAction[],
  disableAnimation?: boolean,
  requestId?: string | null
): AssistantHudMessage => ({
  id: createMessageId("assistant"),
  role: "assistant",
  blocks,
  suggestions,
  actions,
  disableAnimation,
  requestId,
});

const $latestAnimatedAssistantMessageId = createStore<string | null>(null);

export const $assistantMessages = createStore<AssistantHudMessage[]>([]);

export const $assistantStatus = createStore<AssistantHudStatus>("awaiting");
export const $assistantIsBusy = createStore(false);
export const $assistantThinkingText = createStore<string | null>(null);
const initialSessionId = getInitialSessionId();
const $assistantSessionId = createStore<string | null>(initialSessionId);
const $latestRequestId = createStore<string | null>(null);

export const assistantPromptSubmitted = createEvent<string>();
export const assistantPromptSubmittedSilently = createEvent<string>();
export const assistantPoiClickRequested = createEvent<{ id: SightId }>();
export const assistantExternalMessageRequested = createEvent<{
  text: string;
  status?: AssistantHudStatus;
  suggestions?: AssistantSuggestion[];
  actions?: AssistantAction[];
  disableAnimation?: boolean;
}>();
const assistantUserMessageRequested = createEvent<string>();
const assistantPromptAccepted = createEvent<{ text: string; requestId: string }>();
const assistantAwaiting = createEvent();
const assistantThinkingTextReceived = createEvent<string | null>();
const assistantStreamSessionReceived = createEvent<string>();
const assistantStreamMessageReceived = createEvent<EventBusMessage>();
const assistantMessageAdded = createEvent<{
  message: AssistantHudMessage;
  status: AssistantHudStatus;
}>();
export const assistantTextAnimationCompleted = createEvent<string>();
export const assistantSuggestionSelected = createEvent<AssistantSuggestion>();
const assistantActionsReceived = createEvent<AssistantAction[]>();
export const assistantGalleryOpened = createEvent<Array<Photo | Video>>();
export const assistantGalleryClosed = createEvent();
export const assistantViewerOpened = createEvent<{
  items: Array<Photo | Video>;
  index?: number;
}>();
export const assistantViewerClosed = createEvent();
export const assistantViewerIndexChanged = createEvent<number>();

$assistantThinkingText.on(assistantThinkingTextReceived, (_, text) => text);

$latestRequestId.on(assistantPoiClickRequested, () => null);
$assistantThinkingText.reset(assistantPoiClickRequested);

export const $assistantGalleryItems = createStore<Array<Photo | Video>>([])
  .on(assistantGalleryOpened, (_, items) => items)
  .reset(assistantGalleryClosed);

export const $assistantGalleryOpen = createStore(false)
  .on(assistantGalleryOpened, () => true)
  .on(assistantGalleryClosed, () => false)
  .on(assistantViewerOpened, () => false);

export const $assistantViewerItems = createStore<Array<Photo | Video>>([])
  .on(assistantViewerOpened, (_, { items }) => items)
  .reset(assistantViewerClosed);

export const $assistantViewerOpen = createStore(false)
  .on(assistantViewerOpened, () => true)
  .on(assistantViewerClosed, () => false)
  .on(assistantGalleryOpened, () => false);

export const $assistantViewerIndex = createStore(0)
  .on(assistantViewerOpened, (_, payload) => payload.index ?? 0)
  .on(assistantViewerIndexChanged, (_, index) => index)
  .reset(assistantViewerClosed);

const publishEventBusFx = createEffect<EventBusRequest, { sessionId: string }>(
  async (payload) => publishEventBusEvent(payload)
);

let activeStreamSessionId: string | null = null;
let activeStreamController: AbortController | null = null;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timeout = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(timeout);
          resolve();
        },
        { once: true }
      );
    }
  });

const getReconnectDelayMs = (attempt: number) => {
  const base = 1000;
  const max = 20000;
  const exp = Math.min(max, base * Math.pow(2, attempt));
  const jitter = Math.floor(Math.random() * 300);
  return exp + jitter;
};

const connectAssistantStreamFx = createEffect<{ sessionId: string }, void>(
  async ({ sessionId }) => {
    if (!sessionId) {
      return;
    }
    if (activeStreamSessionId === sessionId) {
      return;
    }
    if (activeStreamController) {
      activeStreamController.abort();
    }
    const controller = new AbortController();
    activeStreamSessionId = sessionId;
    activeStreamController = controller;

    let attempt = 0;
    while (!controller.signal.aborted) {
      try {
        await subscribeEventBus(
          sessionId,
          {
            onMessage: (message) => assistantStreamMessageReceived(message),
            onSession: (nextSessionId) =>
              assistantStreamSessionReceived(nextSessionId),
            onError: (error) => {
              if (typeof window !== "undefined") {
                console.warn("event-bus: stream error", error);
              }
            },
          },
          controller.signal
        );
        attempt = 0;
      } catch (error) {
        if (typeof window !== "undefined") {
          console.warn("event-bus: stream disconnected", error);
        }
      }

      if (controller.signal.aborted) {
        break;
      }
      const delayMs = getReconnectDelayMs(attempt);
      attempt += 1;
      await sleep(delayMs, controller.signal);
    }
  }
);

sample({
  clock: assistantPromptSubmitted,
  source: $assistantIsBusy,
  filter: (isBusy, prompt) => !isBusy && Boolean(prompt.trim()),
  fn: (_, prompt) => ({
    text: prompt.trim(),
    requestId: createRequestId(),
  }),
  target: assistantPromptAccepted,
});

sample({
  clock: assistantPromptSubmittedSilently,
  source: $assistantIsBusy,
  filter: (isBusy, prompt) => !isBusy && Boolean(prompt.trim()),
  fn: (_, prompt) => ({
    text: prompt.trim(),
    requestId: createRequestId(),
  }),
  target: assistantPromptAccepted,
});

sample({
  clock: assistantPromptSubmitted,
  target: userInteracted,
});

$assistantMessages.on(assistantPromptAccepted, (messages, payload) => [
  ...messages,
  createUserMessage(payload.text, payload.requestId),
]);

$assistantMessages.on(assistantUserMessageRequested, (messages, text) => [
  ...messages,
  createUserMessage(text),
]);

sample({
  clock: assistantExternalMessageRequested,
  fn: ({ text, status, suggestions, actions, disableAnimation }) => ({
    message: createAssistantMessage(
      toParagraphBlocks(text),
      suggestions ?? [],
      actions ?? [],
      disableAnimation
    ),
    status: status ?? "rendering",
  }),
  target: assistantMessageAdded,
});

$assistantStatus.on(assistantPromptAccepted, () => "sending");
$assistantIsBusy.on(assistantPromptAccepted, () => true);
$assistantThinkingText.reset(assistantPromptAccepted);
$latestRequestId.on(assistantPromptAccepted, (_, payload) => payload.requestId);

$assistantStatus.on(assistantAwaiting, () => "awaiting");

const normalizeSessionId = (sessionId: string | null | undefined): string | null => {
  const trimmed = sessionId?.trim() ?? "";
  return trimmed ? trimmed : null;
};

$assistantSessionId.on(assistantStreamSessionReceived, (_, sessionId) =>
  normalizeSessionId(sessionId)
);

assistantStreamSessionReceived.watch((sessionId) => {
  const normalized = normalizeSessionId(sessionId);
  if (normalized) {
    writeStoredSessionId(normalized);
  }
});

sample({
  clock: assistantStreamSessionReceived,
  filter: (sessionId) => Boolean(sessionId.trim()),
  fn: (sessionId) => ({ sessionId }),
  target: connectAssistantStreamFx,
});

if (initialSessionId) {
  assistantStreamSessionReceived(initialSessionId);
}

sample({
  clock: assistantStreamMessageReceived,
  fn: (message) => message.sessionId,
  target: assistantStreamSessionReceived,
});


sample({
  clock: assistantStreamMessageReceived,
  source: $latestRequestId,
  filter: (latestRequestId, message) =>
    isThinkingMessage(message) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  target: assistantAwaiting,
});

sample({
  clock: assistantStreamMessageReceived,
  source: $latestRequestId,
  filter: (latestRequestId, message) =>
    isThinkingMessage(message) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  fn: (_latestRequestId, message) => {
    const event = message.event;
    if (event.type !== "thinking") {
      throw new Error("Invariant: expected thinking event.");
    }
    const text = event.text?.trim();
    return text ? text : null;
  },
  target: assistantThinkingTextReceived,
});

sample({
  clock: assistantStreamMessageReceived,
  source: $latestRequestId,
  filter: (latestRequestId, message) =>
    isAssistantMessage(message) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  fn: (_latestRequestId, message) => ({
    message: (() => {
      const event = message.event;
      if (event.type !== "assistant_message") {
        throw new Error("Invariant: expected assistant_message event.");
      }
      return createAssistantMessage(
        normalizeMessageBlocks(event.message),
        event.message.suggestions ?? [],
        event.message.actions ?? [],
        false,
        event.requestId ?? null
      );
    })(),
    status: "rendering" as const,
  }),
  target: assistantMessageAdded,
});

sample({
  clock: assistantStreamMessageReceived,
  source: $latestRequestId,
  filter: (latestRequestId, message) =>
    isAssistantMessage(message) &&
    isMatchingRequestId(latestRequestId, message.event.requestId),
  fn: (_latestRequestId, message) => {
    const event = message.event;
    if (event.type !== "assistant_message") {
      throw new Error("Invariant: expected assistant_message event.");
    }
    return event.message.actions ?? [];
  },
  target: assistantActionsReceived,
});

sample({
  clock: assistantPromptAccepted,
  source: {
    currentSight: $currentSight,
    sights: $sightsStore,
    sceneBindings: $sceneBindings,
    sessionId: $assistantSessionId,
    locale: $locale,
    appState: $appState,
  },
  fn: (source, payload): EventBusRequest => {
    const markerDiagnostics =
      source.sceneBindings?.getPoiMarkerDiagnostics?.() ?? [];
    return {
      sessionId: source.sessionId ?? undefined,
      responseFormat: "markdown",
      context: buildEventBusContext({
        currentSight: source.currentSight,
        markers: markerDiagnostics,
        locale: source.locale,
        sceneId: source.appState.sceneId,
      }),
      event: {
        type: "user_message",
        text: payload.text,
        requestId: payload.requestId,
      },
    };
  },
  target: publishEventBusFx,
});

sample({
  clock: publishEventBusFx.doneData,
  fn: (result) => result.sessionId,
  target: assistantStreamSessionReceived,
});

sample({
  clock: publishEventBusFx.failData,
  source: $locale,
  fn: (locale, error) => {
    const i18n = getMessages(locale);
    return {
      message: createAssistantMessage(
        toParagraphBlocks(
          error instanceof Error
            ? error.message
            : i18n.assistant.assistantUnavailable
        ),
        [],
        []
      ),
      status: "error" as const,
    };
  },
  target: assistantMessageAdded,
});

$assistantMessages.on(assistantMessageAdded, (messages, { message }) => [
  ...messages,
  message,
]);

$assistantStatus.on(assistantMessageAdded, (_, { message, status }) =>
  message.role === "assistant" && message.disableAnimation ? "idle" : status
);

$assistantIsBusy.on(assistantMessageAdded, (isBusy, { message }) =>
  message.role === "assistant" && message.disableAnimation ? false : isBusy
);

$assistantThinkingText.reset(assistantMessageAdded);

// Push intro from /api/config as the first assistant message
sample({
  clock: introMessageReceived,
  fn: (intro): { message: AssistantHudMessage; status: AssistantHudStatus } => ({
    message: {
      id: "assistant-initial",
      role: "assistant",
      blocks: normalizeMessageBlocks(intro as AnyAssistantMessage),
      suggestions: intro.suggestions ?? [],
      actions: intro.actions ?? [],
    },
    status: "rendering",
  }),
  target: assistantMessageAdded,
});

$latestAnimatedAssistantMessageId.on(
  assistantMessageAdded,
  (_, { message }) =>
    message.role === "assistant" && message.disableAnimation ? null : message.id
);

const assistantAnimationFinalized = createEvent();

sample({
  clock: assistantTextAnimationCompleted,
  source: $latestAnimatedAssistantMessageId,
  filter: (latestId, messageId) => latestId === messageId,
  target: assistantAnimationFinalized,
});

$assistantStatus.on(assistantAnimationFinalized, () => "idle");
$assistantIsBusy.on(assistantAnimationFinalized, () => false);
$latestAnimatedAssistantMessageId.on(assistantAnimationFinalized, () => null);

const assistantSuggestionAccepted = createEvent<AssistantSuggestion>();
const assistantTourRequested = createEvent<AssistantMessage[]>();
const assistantTourStepActivated = createEvent<AssistantMessage>();
type AssistantHudPayload = {
  message: AssistantHudMessage;
  status: AssistantHudStatus;
};

type SightSuggestion = Extract<AssistantSuggestion, { type: "sight" }>;

const isSightSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is SightSuggestion => suggestion.type === "sight";

const isSightSuggestionWithoutLabel = (
  suggestion: AssistantSuggestion
): suggestion is SightSuggestion => isSightSuggestion(suggestion) && !suggestion.label;

const isSightSuggestionWithLabel = (
  suggestion: AssistantSuggestion
): suggestion is SightSuggestion & { label: string } =>
  isSightSuggestion(suggestion) && Boolean(suggestion.label);

const isMoreSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "more" }> =>
  suggestion.type === "more";

const isTourSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "tour" }> =>
  suggestion.type === "tour";
const isTourPreviousSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "tour-previous" }> =>
  suggestion.type === "tour-previous";

const isTourNextSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "tour-next" }> =>
  suggestion.type === "tour-next";

const isTourContinueSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "tour-continue" }> =>
  suggestion.type === "tour-continue";

const isTourStopSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "tour-stop" }> =>
  suggestion.type === "tour-stop";

const isOrbitSuggestion = (
  suggestion: AssistantSuggestion
): suggestion is Extract<AssistantSuggestion, { type: "orbit" }> =>
  suggestion.type === "orbit";

const buildSightPromptText = (
  sight: Sight | null | undefined,
  locale: Locale,
  fallbackLabel: string
): string => {
  const i18n = getMessages(locale);
  const variants = sight?.promptVariants?.filter((variant) => variant.trim());
  if (variants && variants.length > 0) {
    const variant = variants[Math.floor(Math.random() * variants.length)];
    return formatMessage(variant, { name: fallbackLabel });
  }
  return formatMessage(i18n.assistant.sightPrompt, { name: fallbackLabel });
};

const buildTourStepMessage = (
  sights: Record<SightId, Sight>,
  step: AssistantMessage
): AssistantHudPayload | null => {
  const blocks = step.blocks ?? [];
  if (blocks.length > 0) {
    return {
      message: createAssistantMessage(
        blocks,
        step.suggestions ?? [],
        step.actions ?? []
      ),
      status: "rendering" as const,
    };
  }

  const sightId =
    step.actions.find((action) => action.type === "focus-sight")?.sightId ??
    null;
  if (!sightId) {
    return null;
  }
  const sight = sights[sightId];
  if (!sight) {
    return null;
  }
  const description =
    sight.description ??
    (sight.name
      ? formatMessage(getMessages($locale.getState()).assistant.defaultSightDescription, {
          name: sight.name,
        })
      : "");
  if (!description) {
    return null;
  }

  return {
    message: createAssistantMessage(
      toParagraphBlocks(description),
      step.suggestions ?? [],
      step.actions ?? []
    ),
    status: "rendering" as const,
  };
};

sample({
  clock: assistantSuggestionSelected,
  source: $assistantIsBusy,
  filter: (isBusy) => !isBusy,
  fn: (_, suggestion) => suggestion,
  target: assistantSuggestionAccepted,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isSightSuggestionWithoutLabel,
  fn: (suggestion): SightSelectionPayload => ({
    id: suggestion.sightId,
    smooth: true,
    source: "assistant",
  }),
  target: userSelectedSight,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isSightSuggestionWithLabel,
  fn: (suggestion) => ({
    id: suggestion.sightId,
    smooth: true,
  }),
  target: goToSight,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isSightSuggestionWithLabel,
  fn: (suggestion) => suggestion.label,
  target: assistantPromptSubmitted,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: (_locale, suggestion) => isMoreSuggestion(suggestion),
  source: $locale,
  fn: (locale, suggestion) => {
    const i18n = getMessages(locale);
    console.log(suggestion)
    return suggestion.label || i18n.assistant.continuePrompt
  },
  target: assistantPromptSubmitted,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isTourSuggestion,
  fn: (suggestion) => normalizeTourData(suggestion.data),
  target: assistantTourRequested,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isTourPreviousSuggestion,
  fn: () => ({ smooth: true }),
  target: previousVirtualTourStep,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isTourNextSuggestion,
  fn: () => ({ smooth: true }),
  target: nextVirtualTourStep,
});

sample({
  clock: previousVirtualTourStep,
  source: { locale: $locale, index: $currentVirtualTourIndex },
  filter: ({ index }) => index > 0,
  fn: ({ locale }) => getMessages(locale).assistant.suggestions.previous,
  target: assistantUserMessageRequested,
});

sample({
  clock: nextVirtualTourStep,
  source: {
    locale: $locale,
    index: $currentVirtualTourIndex,
    total: $totalVirtualTourSteps,
  },
  filter: ({ index, total }) => index >= 0 && index < total - 1,
  fn: ({ locale }) => getMessages(locale).assistant.suggestions.next,
  target: assistantUserMessageRequested,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isTourContinueSuggestion,
  fn: () => ({ smooth: true }),
  target: continueVirtualTour,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isTourStopSuggestion,
  target: stopVirtualTour,
});

sample({
  clock: assistantSuggestionAccepted,
  filter: isOrbitSuggestion,
  fn: () => true,
  target: setOrbitAnimationEnabled,
});

sample({
  clock: userSelectedSight,
  source: { sights: $sightsStore, locale: $locale },
  filter: ({ sights }, payload) =>
    payload.source !== "poi" && Boolean(sights[payload.id]),
  fn: ({ sights, locale }, payload) => {
    const sight = sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    return buildSightPromptText(sight, locale, label);
  },
  target: assistantPromptSubmittedSilently,
});

sample({
  clock: userSelectedSight,
  source: {
    sights: $sightsStore,
    locale: $locale,
    sceneBindings: $sceneBindings,
    sessionId: $assistantSessionId,
    appState: $appState,
  },
  filter: (_source, payload) => payload.source === "poi",
  fn: (source, payload): EventBusRequest => {
    const sight = source.sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    const markerDiagnostics =
      source.sceneBindings?.getPoiMarkerDiagnostics?.() ?? [];
    return {
      sessionId: source.sessionId ?? undefined,
      responseFormat: "markdown",
      context: buildEventBusContext({
        currentSight: sight ?? null,
        markers: markerDiagnostics,
        locale: source.locale,
        sceneId: source.appState.sceneId,
      }),
      event: {
        type: "poi_click",
        sightId: payload.id,
        label,
      },
    };
  },
  target: publishEventBusFx,
});

sample({
  clock: assistantPoiClickRequested,
  source: { sights: $sightsStore, locale: $locale },
  fn: ({ sights, locale }, payload) => {
    const sight = sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    return buildSightPromptText(sight, locale, label);
  },
  target: assistantUserMessageRequested,
});

sample({
  clock: userSelectedSight,
  source: { sights: $sightsStore, locale: $locale, isBusy: $assistantIsBusy },
  filter: ({ sights, isBusy }, payload) =>
    !isBusy && payload.source !== "poi" && Boolean(sights[payload.id]),
  fn: ({ sights, locale }, payload) => {
    const sight = sights[payload.id];
    const label = sight?.name ?? payload.id.toString();
    return buildSightPromptText(sight, locale, label);
  },
  target: assistantPromptSubmitted,
});

sample({
  clock: assistantTourRequested,
  filter: (steps) => steps.length > 0,
  target: setVirtualTour,
});

sample({
  clock: $currentVirtualTourIndex,
  source: {
    step: $currentVirtualTourStep,
    isActive: $isVirtualTourActive,
  },
  filter: ({ isActive, step }) => isActive && Boolean(step),
  fn: ({ step }) => step as AssistantMessage,
  target: assistantTourStepActivated,
});

sample({
  clock: assistantTourStepActivated,
  source: $sightsStore,
  filter: (sights, step) => Boolean(buildTourStepMessage(sights, step)),
  fn: (sights, step) => buildTourStepMessage(sights, step) as AssistantHudPayload,
  target: assistantMessageAdded,
});

sample({
  clock: assistantTourStepActivated,
  fn: (step) =>
    (step.actions ?? []).filter((action) => action.type !== "focus-sight"),
  target: assistantActionsReceived,
});

assistantActionsReceived.watch((actions) => {
  actions.forEach((action) => {
    if (action.type === "focus-sight") {
      goToSight({ id: action.sightId, smooth: true });
      return;
    }
    if (action.type === "update-camera-config") {
      updateCameraConfig(action.config ?? {});
      return;
    }
    if (action.type === "update-audio-config") {
      updateAudioConfig(action.config ?? {});
    }
  });
});

const $assistantLastMessage = $assistantMessages.map(
  (messages) => messages[messages.length - 1] ?? null
);

const $assistantRawSuggestions = $assistantLastMessage.map((message) =>
  message && message.role === "assistant" ? message.suggestions : []
);

export const $assistantFilteredSuggestions = combine(
  $assistantRawSuggestions,
  $currentSight,
  $currentVirtualTourIndex,
  $totalVirtualTourSteps,
  $isVirtualTourActive,
  $isVirtualTourPaused,
  $orbitAnimationEnabled,
  $locale,
  (
    suggestions,
    currentSight,
    currentIndex,
    totalSteps,
    isVirtualTourActive,
    isVirtualTourPaused,
    orbitAnimationEnabled,
    locale
  ): AssistantSuggestion[] => {
    const i18n = getMessages(locale);
    const filtered: AssistantSuggestion[] = suggestions
      // .filter((suggestion) => suggestion.type !== "tour")
      // .filter((suggestion) => suggestion.type !== "orbit")
      .filter(
        (suggestion) =>
          !(
            suggestion.type === "sight" &&
            currentSight &&
            currentSight.id === suggestion.sightId
          )
      );

    // if (currentSight && !orbitAnimationEnabled) {
    //   filtered.unshift({ type: "orbit", label: i18n.assistant.suggestions.orbit });
    // }

    if (!isVirtualTourActive) {
      // const hasTourSuggestion = filtered.some(
      //   (suggestion) => suggestion.type === "tour"
      // );
      // if (!hasTourSuggestion) {
      //   filtered.unshift({
      //     type: "tour",
      //     label: i18n.assistant.suggestions.startJourney,
      //     data: kaabaSceneConfig.virtualTour,
      //   });
      // }
      return filtered;
    }

    const tourSuggestions: AssistantSuggestion[] = [];
    if (isVirtualTourPaused) {
      if (currentIndex >= 0 && currentIndex < totalSteps - 1) {
        tourSuggestions.push({
          type: "tour-continue",
          label: i18n.assistant.suggestions.continueStory,
        });
      }
    } else {
      if (currentIndex > 0) {
        tourSuggestions.push({
          type: "tour-previous",
          label: i18n.assistant.suggestions.previous,
        });
      }
      if (currentIndex >= 0 && currentIndex < totalSteps - 1) {
        tourSuggestions.push({
          type: "tour-next",
          label: i18n.assistant.suggestions.next,
        });
      }
    }
    tourSuggestions.push({
      type: "tour-stop",
      label: i18n.assistant.suggestions.pauseTour,
    });

    return [...tourSuggestions, ...filtered];
  }
);
